/**
 * This component will show the booking info and calculated total price.
 * I.e. dates and other details related to payment decision in receipt format.
 */
import React, { useState } from 'react';
import { oneOf, string } from 'prop-types';
import classNames from 'classnames';
import { MdCheckCircle } from 'react-icons/md';
import { FaTimesCircle } from 'react-icons/fa';
import { FormattedMessage, intlShape, injectIntl } from '../../util/reactIntl';
import {
  propTypes,
  LISTING_UNIT_TYPES,
  LINE_ITEM_CUSTOMER_COMMISSION,
  LINE_ITEM_PROVIDER_COMMISSION,
} from '../../util/types';
import Collapsible from 'react-collapsible';
import LineItemBookingPeriod from './LineItemBookingPeriod';
import LineItemBasePriceMaybe from './LineItemBasePriceMaybe';
import LineItemSubTotalMaybe from './LineItemSubTotalMaybe';
import LineItemShippingFeeMaybe from './LineItemShippingFeeMaybe';
import LineItemPickupFeeMaybe from './LineItemPickupFeeMaybe';
import LineItemCustomerCommissionMaybe from './LineItemCustomerCommissionMaybe';
import LineItemCustomerCommissionRefundMaybe from './LineItemCustomerCommissionRefundMaybe';
import LineItemProviderCommissionMaybe from './LineItemProviderCommissionMaybe';
import LineItemProviderCommissionRefundMaybe from './LineItemProviderCommissionRefundMaybe';
import LineItemRefundMaybe from './LineItemRefundMaybe';
import LineItemTotalPrice from './LineItemTotalPrice';
import LineItemUnknownItemsMaybe from './LineItemUnknownItemsMaybe';
import { IoIosArrowDown } from 'react-icons/io';
import { IoIosArrowUp } from 'react-icons/io';
import css from './OrderBreakdown.module.css';
import FieldTextInput from '../FieldTextInput/FieldTextInput';
import { loadInitialDataForStripePayments } from '../../containers/CheckoutPage/CheckoutPageWithPayment';
import { PrimaryButton } from '../Button/Button';
import { verifyPromoCode } from '../../containers/CheckoutPage/CheckoutPage.duck';
import { useDispatch } from 'react-redux';
import { IconSpinner } from '..';
import LineItemPromoCodeMaybe from './LineItemPromoCodeMaybe';

export const OrderBreakdownComponent = props => {
  const {
    rootClassName,
    className,
    userRole,
    transaction,
    booking,
    intl,
    dateType,
    timeZone,
    currency,
    marketplaceName,
    pageData,
    fetchSpeculatedTransaction,
    checkoutPage,
    currentUser,

    applyPromoCodeInProgress,
    hideShippingFee,
  } = props;
  const author = pageData?.listing?.author;
  const authorId = author?.id?.uuid;
  const isPopNouvelleUser = authorId === process.env.REACT_APP_POP_NOUVELLE_USER;

  const [open, setOpen] = useState(false);



  const dispatch = useDispatch();
  const isCustomer = userRole === 'customer';
  const isProvider = userRole === 'provider';
  const { attributes } = currentUser || {};
  const { profile } = attributes || {};
  const { metadata, publicData } = profile || {};


  const lineItems = transaction.attributes.lineItems;


  const isCustomerCommision = lineItems?.find(item => item.code === LINE_ITEM_CUSTOMER_COMMISSION);
  // console.log('isCustomerCommision', isCustomerCommision);
  const unitLineItem = lineItems?.find(
    item => LISTING_UNIT_TYPES.includes(item.code) && !item.reversal
  );
  // Line-item code that matches with base unit: day, night, hour, item
  const lineItemUnitType = unitLineItem?.code;

  const hasCommissionLineItem = lineItems.find(item => {
    const hasCustomerCommission = isCustomer && item.code === LINE_ITEM_CUSTOMER_COMMISSION;
    const hasProviderCommission = isProvider && item.code === LINE_ITEM_PROVIDER_COMMISSION;
    return (hasCustomerCommission || hasProviderCommission) && !item.reversal;
  });

  const classes = classNames(rootClassName || css.root, className);

  /**
   * OrderBreakdown contains different line items:
   *
   * LineItemBookingPeriod: prints booking start and booking end types. Prop dateType
   * determines if the date and time or only the date is shown
   *
   * LineItemShippingFeeMaybe: prints the shipping fee (combining additional fee of
   * additional items into it).
   *
   * LineItemShippingFeeRefundMaybe: prints the amount of refunded shipping fee
   *
   * LineItemBasePriceMaybe: prints the base price calculation for the listing, e.g.
   * "$150.00 * 2 nights $300"
   *
   * LineItemUnknownItemsMaybe: prints the line items that are unknown. In ideal case there
   * should not be unknown line items. If you are using custom pricing, you should create
   * new custom line items if you need them.
   *
   * LineItemSubTotalMaybe: prints subtotal of line items before possible
   * commission or refunds
   *
   * LineItemRefundMaybe: prints the amount of refund
   *
   * LineItemCustomerCommissionMaybe: prints the amount of customer commission
   * The default transaction process used by this template doesn't include the customer commission.
   *
   * LineItemCustomerCommissionRefundMaybe: prints the amount of refunded customer commission
   *
   * LineItemProviderCommissionMaybe: prints the amount of provider commission
   *
   * LineItemProviderCommissionRefundMaybe: prints the amount of refunded provider commission
   *
   * LineItemTotalPrice: prints total price of the transaction
   *
   */

  if (applyPromoCodeInProgress) {
    return (
      <div className={css.iconSpinnerContainer}>
        {' '}
        <IconSpinner />
      </div>
    );
  }
  return (
    <div className={classes}>
      <LineItemBookingPeriod
        booking={booking}
        code={lineItemUnitType}
        dateType={dateType}
        timeZone={timeZone}
      />
      {isCustomer && isCustomerCommision ? (
        <Collapsible
          trigger={
            <div
              className={css.trigger}
              onClick={() => {
                setOpen(!open);
              }}
            >
              <LineItemSubTotalMaybe
                lineItems={lineItems}
                code={lineItemUnitType}
                userRole={userRole}
                intl={intl}
                open={open}
                marketplaceCurrency={currency}
                isCustomer={isCustomer}
              />
            </div>
          }
        >
          <LineItemBasePriceMaybe lineItems={lineItems} code={lineItemUnitType} intl={intl} />
          <LineItemCustomerCommissionMaybe
            lineItems={lineItems}
            isCustomer={isCustomer}
            marketplaceName={marketplaceName}
            intl={intl}
          />
          <hr className={css.totalDivider} />
        </Collapsible>
      ) : (
        <LineItemBasePriceMaybe lineItems={lineItems} code={lineItemUnitType} intl={intl} />
      )}
      {!hideShippingFee && <LineItemShippingFeeMaybe lineItems={lineItems} intl={intl} isCustomer={isCustomer} />}
      <LineItemPromoCodeMaybe lineItems={lineItems} intl={intl} isCustomer={isCustomer} />
      <LineItemPickupFeeMaybe lineItems={lineItems} intl={intl} />
      <LineItemUnknownItemsMaybe lineItems={lineItems} isProvider={isProvider} intl={intl} />

      <LineItemRefundMaybe lineItems={lineItems} intl={intl} marketplaceCurrency={currency} />

      <LineItemCustomerCommissionRefundMaybe
        lineItems={lineItems}
        isCustomer={isCustomer}
        marketplaceName={marketplaceName}
        intl={intl}
      />

      {/* <LineItemProviderCommissionMaybe
        lineItems={lineItems}
        isProvider={isProvider}
        marketplaceName={marketplaceName}
        intl={intl}
      />
      <LineItemProviderCommissionRefundMaybe
        lineItems={lineItems}
        isProvider={isProvider}
        marketplaceName={marketplaceName}
        intl={intl}
      /> */}


      <LineItemTotalPrice transaction={transaction} isProvider={isProvider} intl={intl} />

      {/* {hasCommissionLineItem ? (
        <span className={css.feeInfo}>
          <FormattedMessage id="OrderBreakdown.commissionFeeNote" />
        </span>
      ) : null} */}
    </div>
  );
};

OrderBreakdownComponent.defaultProps = {
  rootClassName: null,
  className: null,
  booking: null,
  dateType: null,
};

OrderBreakdownComponent.propTypes = {
  rootClassName: string,
  className: string,

  marketplaceName: string.isRequired,
  userRole: oneOf(['customer', 'provider']).isRequired,
  transaction: propTypes.transaction.isRequired,
  booking: propTypes.booking,
  dateType: propTypes.dateType,

  // from injectIntl
  intl: intlShape.isRequired,
};

const OrderBreakdown = injectIntl(OrderBreakdownComponent);

OrderBreakdown.displayName = 'OrderBreakdown';

export default OrderBreakdown;
